import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const ProductWrapper = styled.section`
  background: var(--bg-primary);
  padding: 140px 20px;
  position: relative;
  overflow: hidden;

  /* Large background text */
  &::before {
    content: "PRODUCTS";
    position: absolute;
    bottom: 40px;
    right: -100px;
    font-family: Poppins;
    font-size: 250px;
    font-weight: 900;
    color: var(--text-primary);
    opacity: 0.02;
    pointer-events: none;
    transform: rotate(-90deg);
    transform-origin: right bottom;
  }

  .productContainer {
    max-width: 1400px;
    width: 100%;
    margin: 0 auto;
    position: relative;
    z-index: 1;

    .sectionHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-bottom: 60px;
      gap: 40px;

      .headerLeft {
        .sectionLabel {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;

          .line {
            width: 40px;
            height: 3px;
            background: var(--accent);
            border-radius: 2px;
          }

          span {
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 3px;
            color: var(--accent);
          }
        }

        h2 {
          font-family: Poppins;
          font-size: 56px;
          font-weight: 800;
          line-height: 1.1;
          color: var(--text-primary);
          margin: 0;

          .gradient {
            background: linear-gradient(135deg, var(--accent) 0%, var(--primary) 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
        }
      }
    }

    .slick-slider {
      margin: 0 -15px;
    }

    .slick-slide {
      padding: 15px;
    }

    .slick-prev,
    .slick-next {
      width: 56px;
      height: 56px;
      background: var(--card-bg);
      border: 2px solid var(--card-border);
      border-radius: 50%;
      z-index: 10;
      transition: all 0.3s ease;

      &::before {
        font-size: 20px;
        color: var(--text-primary);
      }

      &:hover {
        background: var(--accent);
        border-color: var(--accent);

        &::before {
          color: #fff;
        }
      }
    }

    .slick-prev {
      left: -30px !important;
    }

    .slick-next {
      right: -30px !important;
    }

    .productWrapper {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-around;
      gap: 50px 0px;
    }

    .buttonWrapper {
      margin-top: 60px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    @media (max-width: 1024px) {
      .sectionHeader {
        flex-direction: column;
        align-items: flex-start;

        .headerLeft h2 {
          font-size: 42px;
        }
      }

      .slick-prev, .slick-next {
        display: none !important;
      }
    }

    @media (max-width: 640px) {
      .sectionHeader .headerLeft h2 {
        font-size: 32px;
      }
    }
  }
`;

export const ProductItemWrapper = styled.div`
  border-radius: 20px;
  background: var(--card-bg);
  overflow: hidden;
  border: 1px solid var(--card-border);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  height: 440px;
  display: flex;
  flex-direction: column;

  /* Glassmorphism overlay on hover */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0) 50%,
      rgba(255, 255, 255, 0.05) 100%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 1;
  }

  /* Product badges */
  .product-badge {
    position: absolute;
    top: 16px;
    left: 16px;
    padding: 6px 14px;
    border-radius: 8px;
    font-family: Poppins;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: #fff;
    z-index: 10;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(4px);

    &.new {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    }

    &.hot {
      background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    }

    &.featured {
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    }

    &.bestseller {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    }
  }

  &.has-badge.badge-new {
    border-color: rgba(16, 185, 129, 0.25);
  }

  &.has-badge.badge-hot {
    border-color: rgba(239, 68, 68, 0.25);
  }

  &.has-badge.badge-featured {
    border-color: rgba(139, 92, 246, 0.25);
  }

  &.has-badge.badge-bestseller {
    border-color: rgba(245, 158, 11, 0.25);
  }

  /* Top accent line */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent), var(--primary), var(--accent));
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 2;
  }

  .imageWrapper {
    height: 200px;
    overflow: hidden;
    background: var(--bg-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: background 0.4s ease, box-shadow 0.4s ease;

    /* Skeleton loader */
    &.loading {
      &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          var(--bg-secondary) 0%,
          var(--card-bg) 50%,
          var(--bg-secondary) 100%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 1.5s ease-in-out infinite;
      }
    }

    .image-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to bottom,
        transparent 60%,
        rgba(0, 0, 0, 0.02) 100%
      );
      opacity: 0;
      transition: opacity 0.4s ease;
      pointer-events: none;
    }

    img {
      height: 170px;
      width: auto;
      max-width: 85%;
      object-fit: contain;
      transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                  filter 0.4s ease;
      position: relative;
      z-index: 1;
    }

    .no-image {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: var(--text-secondary);
      opacity: 0.5;

      svg {
        width: 48px;
        height: 48px;
        stroke-width: 1;
      }

      span {
        font-family: Poppins;
        font-size: 12px;
      }
    }
  }

  .productDetail {
    padding: 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 2;

    .categoryTag {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: var(--text-secondary);
      font-family: Poppins;
      font-size: 11px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      margin-bottom: 8px;

      &::before {
        content: "";
        width: 6px;
        height: 6px;
        background: var(--accent);
        border-radius: 50%;
      }
    }

    h1 {
      color: var(--text-primary);
      font-family: Poppins;
      font-size: 17px;
      font-weight: 600;
      line-height: 1.4;
      margin-bottom: 6px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      transition: color 0.3s ease;
    }

    .modelNo {
      color: var(--text-secondary);
      font-family: Poppins;
      font-size: 12px;
      font-weight: 500;
      margin-bottom: 8px;
      opacity: 0.7;
    }

    p {
      color: var(--text-secondary);
      font-family: Poppins;
      font-size: 13px;
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      flex: 1;
      margin-bottom: 12px;
    }

    .viewBtn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 12px 20px;
      background: transparent;
      color: var(--accent);
      font-family: Poppins;
      font-size: 13px;
      font-weight: 600;
      border: 2px solid var(--card-border);
      border-radius: 10px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      margin-top: auto;

      svg {
        width: 16px;
        height: 16px;
        transition: transform 0.3s ease;
      }
    }
  }

  @media (max-width: 640px) {
    height: 420px;

    .imageWrapper {
      height: 180px;

      img {
        height: 150px;
      }
    }

    .productDetail {
      padding: 16px;

      h1 {
        font-size: 15px;
      }

      .viewBtn {
        padding: 10px 16px;
        font-size: 12px;
      }
    }
  }
`;

export const ProductButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 18px 40px;
  background: transparent;
  color: var(--text-primary);
  font-family: Poppins;
  font-size: 16px;
  font-weight: 600;
  border: 2px solid var(--text-primary);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background: var(--accent);
    transition: width 0.3s ease;
    z-index: -1;
  }

  &:hover {
    color: #fff;
    border-color: var(--accent);

    &::before {
      width: 100%;
    }
  }

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(5px);
  }
`;

export const Wrapper = styled.div`
  position: relative;
  cursor: pointer;

  a {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 10;
  }

  &:hover ${ProductItemWrapper} {
    transform: translateY(-8px);
    box-shadow:
      0 20px 40px rgba(0, 0, 0, 0.08),
      0 8px 16px rgba(0, 0, 0, 0.06);
    border-color: var(--accent);

    &::before {
      opacity: 1;
      animation: ${shimmer} 2s linear infinite;
    }

    &::after {
      opacity: 1;
    }

    .imageWrapper {
      background: rgba(217, 58, 53, 0.06);
      box-shadow: inset 0 0 0 3px rgba(217, 58, 53, 0.15);

      img {
        transform: scale(1.12);
      }
    }

    .productDetail .viewBtn {
      background: var(--accent);
      color: #fff;
      border-color: var(--accent);

      svg {
        transform: translateX(4px);
      }
    }
  }
`;
